/**
 * useMotionBudget — Mobile-aware animation budget hook (O-4).
 *
 * Detects mobile viewport and prefers-reduced-motion to return
 * simplified Framer Motion variants. Prevents jank on low-end
 * devices by disabling stagger and using faster transitions.
 *
 * Usage:
 *   const { containerVariants, itemVariants, isMobile, shouldAnimate } = useMotionBudget()
 *   <motion.div variants={containerVariants} initial="hidden" animate="visible">
 *     <motion.div variants={itemVariants}>...</motion.div>
 *   </motion.div>
 */

import { useState, useEffect, useMemo } from 'react'

const MOBILE_BREAKPOINT = 768

/** Static variant sets — no hook needed for non-reactive usage */
export const MOTION_BUDGET = {
  /** Full desktop animations: stagger + spring */
  desktop: {
    container: {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
    },
    item: {
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', bounce: 0.4, duration: 0.8 },
      },
    },
  },
  /** Simplified mobile animations: no stagger, faster tween */
  mobile: {
    container: {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { staggerChildren: 0 } },
    },
    item: {
      hidden: { opacity: 0, y: 8 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { type: 'tween', duration: 0.25, ease: 'easeOut' },
      },
    },
  },
  /** No animations at all */
  none: {
    container: {
      hidden: { opacity: 1 },
      visible: { opacity: 1 },
    },
    item: {
      hidden: { opacity: 1, y: 0 },
      visible: { opacity: 1, y: 0 },
    },
  },
} as const

type MotionTier = 'desktop' | 'mobile' | 'none'

function getMotionTier(isMobile: boolean, prefersReduced: boolean): MotionTier {
  if (prefersReduced) return 'none'
  if (isMobile) return 'mobile'
  return 'desktop'
}

export function useMotionBudget() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false,
  )

  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  // Listen for viewport changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    // Use matchMedia for efficient breakpoint detection
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const handleMqlChange = (e: MediaQueryListEvent) => setIsMobile(e.matches)

    if (mql.addEventListener) {
      mql.addEventListener('change', handleMqlChange)
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', handleResize)
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handleMqlChange)
      } else {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [])

  // Listen for prefers-reduced-motion changes
  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches)

    if (mql.addEventListener) {
      mql.addEventListener('change', handler)
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handler)
      }
    }
  }, [])

  const tier = getMotionTier(isMobile, prefersReduced)
  const budget = MOTION_BUDGET[tier]

  return useMemo(
    () => ({
      /** Current motion tier */
      tier,
      /** Whether viewport is mobile-sized */
      isMobile,
      /** Whether animations should play at all */
      shouldAnimate: tier !== 'none',
      /** Whether stagger is enabled (desktop only) */
      shouldStagger: tier === 'desktop',
      /** Container variants (stagger on desktop, instant on mobile) */
      containerVariants: budget.container,
      /** Item variants (spring on desktop, fast tween on mobile) */
      itemVariants: budget.item,
    }),
    [tier, isMobile, budget],
  )
}
